
(() => {
  'use strict';

  /* =========================================
     WAMBOLT SYSTEM SPECIFICATIONS
     ========================================= */

  // Prevent duplicate installation when Shopify's
  // bundled section script also runs.
  if (window.wamboltSpecificationsTabsInstalled) return;
  window.wamboltSpecificationsTabsInstalled = true;

  const ROOT = '[data-wambolt-specifications]';
  const TAB = '[data-wambolt-spec-tab]';
  const PANEL = '[data-wambolt-spec-panel]';

  let touchStart = null;
  let refreshFrame = 0;

  /* =========================================
     TAB LOOKUP
     ========================================= */

  function findTab(node) {
    const element = node instanceof Element
      ? node
      : node?.parentElement;

    const tab = element?.closest(TAB);
    return tab && tab.closest(ROOT) ? tab : null;
  }

  function getEntries(root) {
    const tabs = Array.from(root.querySelectorAll(TAB));
    const panels = Array.from(root.querySelectorAll(PANEL));

    if (!tabs.length || tabs.length !== panels.length) return [];

    const byId = new Map(
      panels.map(panel => [panel.id, panel])
    );

    const entries = tabs.map(tab => ({
      tab,
      panel: byId.get(tab.getAttribute('aria-controls'))
    }));

    return entries.every(entry => entry.panel) ? entries : [];
  }

  /* =========================================
     LAYOUT REFRESH
     ========================================= */

  function requestLayoutRefresh() {
    if (refreshFrame) {
      cancelAnimationFrame(refreshFrame);
    }

    refreshFrame = requestAnimationFrame(() => {
      refreshFrame = 0;

      window.wamboltLenis?.resize?.();
      window.ScrollTrigger?.refresh?.();
    });
  }

  /* =========================================
     ACTIVATE TAB
     ========================================= */

  function activate(tab, focus = false, refresh = true) {
    const root = tab?.closest(ROOT);
    if (!root) return;

    const entries = getEntries(root);
    const selectedIndex = entries.findIndex(
      entry => entry.tab === tab
    );

    if (selectedIndex < 0) return;

    const changed = entries.some((entry, index) =>
      index === selectedIndex &&
      entry.tab.getAttribute('aria-selected') !== 'true'
    );

    entries.forEach(({ tab: currentTab, panel }, index) => {
      const selected = index === selectedIndex;

      currentTab.setAttribute(
        'aria-selected',
        String(selected)
      );

      currentTab.tabIndex = selected ? 0 : -1;

      panel.hidden = !selected;
      panel.toggleAttribute('inert', !selected);
    });

    if (focus) tab.focus();

    if (changed && refresh) {
      requestLayoutRefresh();
    }
  }

  /* =========================================
     NORMALIZE CURRENT PAGE
     ========================================= */

  function normalize(container = document) {
    const roots = [];

    if (
      container instanceof Element &&
      container.matches(ROOT)
    ) {
      roots.push(container);
    }

    container.querySelectorAll?.(ROOT).forEach(root => {
      roots.push(root);
    });

    roots.forEach(root => {
      const entries = getEntries(root);

      const selected = entries.find(entry =>
        entry.tab.getAttribute('aria-selected') === 'true'
      ) || entries[0];

      if (selected) {
        activate(selected.tab, false, false);
      }
    });
  }

  /* =========================================
     CLICK HANDLING
     ========================================= */

  // Delegation remains attached to document,
  // allowing Barba to replace the tab markup.
  document.addEventListener('click', event => {
    const tab = findTab(event.target);
    if (!tab) return;

    event.preventDefault();
    activate(tab);
  }, true);

  /* =========================================
     TOUCH / PEN HANDLING
     ========================================= */

  document.addEventListener('pointerdown', event => {
    if (
      event.pointerType !== 'touch' &&
      event.pointerType !== 'pen'
    ) {
      return;
    }

    const tab = findTab(event.target);

    touchStart = tab ? {
      tab,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY
    } : null;
  }, true);

  document.addEventListener('pointerup', event => {
    if (
      !touchStart ||
      touchStart.pointerId !== event.pointerId
    ) {
      return;
    }

    const start = touchStart;
    touchStart = null;

    const travel = Math.hypot(
      event.clientX - start.x,
      event.clientY - start.y
    );

    if (
      travel < 12 &&
      findTab(event.target) === start.tab
    ) {
      activate(start.tab);
    }
  }, true);

  document.addEventListener('pointercancel', () => {
    touchStart = null;
  }, true);

  /* =========================================
     KEYBOARD NAVIGATION
     ========================================= */

  document.addEventListener('keydown', event => {
    const tab = findTab(event.target);
    if (!tab) return;

    const root = tab.closest(ROOT);
    const entries = getEntries(root);

    const index = entries.findIndex(
      entry => entry.tab === tab
    );

    if (index < 0) return;

    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % entries.length;
        break;

      case 'ArrowLeft':
        next = (index - 1 + entries.length) % entries.length;
        break;

      case 'Home':
        next = 0;
        break;

      case 'End':
        next = entries.length - 1;
        break;

      default:
        return;
    }

    event.preventDefault();
    activate(entries[next].tab, true);
  }, true);

  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener('shopify:block:select', event => {
    const blockId = event.detail?.blockId;
    const directTab = findTab(event.target);

    if (directTab) {
      activate(directTab);
      return;
    }

    if (!blockId) return;

    const tab = Array.from(
      document.querySelectorAll(TAB)
    ).find(candidate =>
      candidate.id.endsWith(`-${blockId}`)
    );

    if (tab) activate(tab);
  });

  document.addEventListener('shopify:section:load', event => {
    requestAnimationFrame(() => {
      normalize(event.target);
    });
  });

  document.addEventListener(
    'shopify:section:reorder',
    () => normalize()
  );

  /* =========================================
     BARBA PAGE LIFECYCLE
     ========================================= */

  // The Wambolt transition runtime dispatches
  // this custom event on window, not document.
  window.addEventListener(
    'wambolt:page:enter',
    () => normalize()
  );

  window.addEventListener(
    'pageshow',
    () => normalize()
  );

  /* =========================================
     INITIAL PAGE
     ========================================= */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => normalize(),
      { once: true }
    );
  } else {
    normalize();
  }
})();
